import { DatePipe, NgTemplateOutlet } from '@angular/common';
import type { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, OnInit, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { isOpenRoute } from '../open/open-progress';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { StepUpCeremonyService, messageKeyBaseFor, parseProblemDetails } from '@toybank/ui';
import { Subscription, filter, map, of, retry, throwError, timeout, timer } from 'rxjs';
import { ApiService, isActiveTownsimSession, type Me } from '../api.service';
import { AppComponent } from '../app.component';
import { PaymentDraftService } from '../features/payment-draft.service';
import { DemoPersonaSwitchService } from '../demo/demo-persona-switch.service';
import { personaSwitchAppliesToHost } from '../demo/persona-switch.model';
import {
  DEFAULT_LOGIN_PRESENTATION,
  LOGIN_PRESENTATION,
  LoginScreenComponent,
  type LoginPresentation,
  type LoginSubmitStrategy,
} from '../login/login-screen.component';
import { LoginNoticeService } from '../../../../shared/login-notice/login-notice.service';
import { BankFeedService } from './bank-feed.service';
import { composeDebugLoginNotice, drawerStackedSignal } from './debug-login-notice';
import { type DebugDrawersController, TB_DEBUG_DRAWERS } from './debug-drawers.model';
import { HttpLogService } from './http-log.service';
import { MockPhoneService, PhoneMessage } from './phone.service';
import { type DebugLoginTarget, type Persona, PersonaService } from './persona.service';
import { TestAccountBadgeComponent } from './test-account-badge.component';
import { TestAccountInfoDialogComponent } from './test-account-info-dialog.component';
import { TestAccountsService, curatorLine } from './test-accounts.service';
import {
  type PickerAudience,
  audienceOfPersona,
  groupPickerAccounts,
  readRecentAccounts,
  recordRecentAccount,
  splitAccountLabel,
} from './recent-accounts';

// Login and fake-device step-up are deliberately separate. User-pick installs a real BFF cookie
// session; only a later, operation-bound server challenge can activate the mock phone.
type Phase = 'login' | 'portal';
type DebugSurfaceTarget = 'customer' | DebugLoginTarget | 'simulator';

/**
 * How long the load-time session probe may hang before the wrapper gives up and offers the picker.
 *
 * The picker is HIDDEN while the probe is outstanding (showing "sign in" to someone who is already
 * signed in is the whole defect being fixed), so an unanswered probe would otherwise leave a
 * visitor with no way to sign in at all. A bound turns that into a bad-but-recoverable outcome:
 * worst case the picker appears a few seconds late, exactly as it did before this fix. Generous
 * relative to what the endpoint actually costs -- one unfenced session read, measured at ~60ms
 * against the live stack -- because a slow answer is still a real answer worth waiting for.
 */
const SESSION_PROBE_TIMEOUT_MS = 4_000;

export interface TargetOption {
  id: DebugSurfaceTarget;
  label: string;
}

// Every id the picker can ever render, across every debug host -- used to validate the initial
// ?target= query param / host-derived preselection independent of which subset buildTargetOptions
// currently shows (see its docstring for why the visible id for the two portal cells swaps).
const ALL_DEBUG_SURFACE_TARGETS: readonly DebugSurfaceTarget[] =
  ['customer', 'portal', 'demo-portal', 'backoffice', 'demo-backoffice', 'simulator'];

/**
 * The 5th, extra-wide portal-picker button -- links to the TownSim debug lane (owner UX
 * request). Deliberately kept OUT of buildTargetOptions()'s 2x2 grid array: that function's
 * 4-element shape is pinned by debug-shell.component.spec.ts's exact-equality assertions, and
 * "Simulator" is not one of the four ordinary customer/staff destinations documented in the
 * README's 2x2 table. The template renders it as a separate wide button spanning beneath the
 * grid, styled and iconed via the same LOGIN_PRESENTATION config as the other four.
 */
export const SIMULATOR_TARGET: TargetOption = { id: 'simulator', label: 'Simulator' };

function isDebugSurfaceTarget(value: string | null): value is DebugSurfaceTarget {
  return (ALL_DEBUG_SURFACE_TARGETS as readonly string[]).includes(value ?? '');
}

/**
 * True when THIS debug wrapper instance is itself serving the demo customer host
 * (debug.demo.localhost), mirroring EnvModeFilter's server-side host->mode table
 * (`demo.localhost` / `debug.demo.localhost` -> demo; everything else -> dev/sim).
 */
export function isDemoDebugHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  return host === 'debug.demo.localhost' || host.startsWith('debug.demo.');
}

/**
 * Build the four login-target picker buttons for the 2x2 Normal/Demo x Portal/Backoffice grid
 * (owner UX request: the picker previously had no way to reach the normal, non-demo customer
 * portal at all -- only whichever flavor happened to match the currently-served debug host).
 *
 * The customer-portal login has two flavors (normal = toybank.localhost, demo = demo.localhost),
 * but a single debug-wrapper instance is only ever ONE origin at a time (ADR-0105). Whichever
 * flavor matches the CURRENT debug host logs in inline (id 'customer' -- no redirect, so the
 * mock-phone / HTTP-log panels stay live for step-up testing, same as before this change). The
 * OTHER flavor has to hop to a different origin, so it goes through the same named-handoff
 * mechanism backoffice / demo-backoffice already use (`portal` -> toybank.localhost, `demo-portal`
 * -> demo.localhost; see DebugLoginProperties#defaultHandoffTargets). Which id lands in which grid
 * cell therefore depends on the host serving the wrapper right now; the LABEL always tells the
 * truth about where the click goes, on every one of the five debug hosts (ADR-0105's README).
 */
export function buildTargetOptions(demoHost: boolean): readonly TargetOption[] {
  return [
    { id: demoHost ? 'portal' : 'customer', label: 'Main Portal' },
    { id: 'backoffice', label: 'Backoffice' },
    { id: demoHost ? 'customer' : 'demo-portal', label: 'Demo Portal' },
    { id: 'demo-backoffice', label: 'Demo Backoffice' },
  ];
}

function initialTarget(): DebugSurfaceTarget {
  if (typeof window === 'undefined') return 'customer';
  const host = window.location.hostname.toLowerCase();
  if (host === 'debug.backoffice.localhost') return 'backoffice';
  if (host === 'debug.demobackoffice.localhost') return 'demo-backoffice';
  try {
    const raw = new URLSearchParams(window.location.search).get('target')?.trim().toLowerCase() ?? null;
    const normalized = raw === 'demobackoffice' || raw === 'demo_backoffice' ? 'demo-backoffice' : raw;
    return isDebugSurfaceTarget(normalized) ? normalized : 'customer';
  } catch {
    return 'customer';
  }
}

/**
 * Returns true for the sim renderer hosts, including non-localhost debug deployments.
 */
export function isSimHostname(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === 'debug.sim.localhost' || host.startsWith('debug.sim.') || host.startsWith('sim.');
}

function isSimHost(): boolean {
  return typeof window !== 'undefined' && isSimHostname(window.location.hostname);
}

/**
 * Pure host-label transform for the "Simulator" picker button: swaps the lane label in
 * `debug.<lane>.<suffix>` for `sim`, mirroring isDemoDebugHost's flexible debug.<lane>.<suffix>
 * shape (works on a non-`.localhost` debug suffix too, e.g. in a test environment). Falls back to
 * the canonical `debug.sim.localhost` when the current host doesn't look like a debug host at all.
 */
export function simHostFromHostname(hostname: string): string {
  const labels = hostname.toLowerCase().split('.');
  return labels[0] === 'debug' && labels.length > 2
    ? ['debug', 'sim', ...labels.slice(2)].join('.')
    : 'debug.sim.localhost';
}

/**
 * Full origin+path for this debug wrapper's sibling sim host. There is no backend
 * handoff-ticket mechanism for the simulator lane yet -- DebugLoginProperties.handoffTargets
 * (edge-bff) only configures the four ordinary customer/staff destinations (portal, demo-portal,
 * backoffice, demo-backoffice). Until that lands (backend follow-up), selecting "Simulator" and
 * continuing does a plain top-level navigation directly to the TownSim renderer.
 */
export function debugSimHostUrl(authority?: unknown, health?: unknown): string {
  if (typeof window === 'undefined') return 'http://debug.sim.localhost/town/';
  const { protocol, hostname, port } = window.location;
  const host = simHostFromHostname(hostname);
  return debugTownsimEntryUrl(`${protocol}//${host}${port ? ':' + port : ''}/town/`, authority, health);
}

/** The published health projection has no mode; directory authority supplies that binding. */
export function debugTownsimEntryUrl(baseUrl: string, authority?: unknown, health?: unknown): string {
  const record = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);
  if (record(authority) && authority['authorityMode'] === 'DYNAMIC' && authority['selectionRequired'] === true
      && record(health) && health['contract'] === 'townsim-step-in-health'
      && health['version'] === 1 && health['ok'] === true) {
    const url = new URL(baseUrl, typeof window === 'undefined' ? 'http://debug.sim.localhost' : window.location.href);
    url.searchParams.set('sim', 'edge');
    return url.toString();
  }
  return baseUrl;
}

/**
 * Decides whether a failed `GET /bff/demo/session` liveness probe (resumePortalFromDemoSession)
 * deserves a retry before the caller trusts it as proof the root session is dead.
 *
 * A 401 is the one definitive "not logged in" ERROR this endpoint can give (an expired or
 * authority-rejected session; an anonymous visitor on a demo host now gets a 200
 * `{authenticated:false}` instead, handled on the success path) — never worth retrying. Every other failure (a 5xx, a network error, an
 * undefined status) is not proof of a dead session; this shared local box runs 45+ services under
 * concurrent load and edge-bff's own logs show the same class of transient failure racing sibling
 * `/home` requests elsewhere. Trusting one of those as "session is dead" is exactly the regression
 * this function exists to prevent — see resumePortalFromDemoSession's doc comment.
 */
export function shouldRetryDemoSessionProbe(status: number | undefined): boolean {
  return status !== 401;
}

function hasAuthenticatedSubject(me: Me): boolean {
  const identity = me as Me & { sub?: string | null; subject?: string | null };
  return [identity.sub, identity.subject].some((sub) => typeof sub === 'string' && sub.trim().length > 0);
}

/**
 * D3 fix: returns true when this document is loaded as a townsim portal iframe.
 * world-app.mjs appends ?townsim_portal=1 to the portal iframe src after a successful
 * townsim-org swap. We use this as an EXPLICIT SIGNAL, validated below against the live
 * BFF session state (GET /bff/townsim/session must confirm an active TOWNSIM_ORG
 * PERSONA session), before
 * skipping the persona picker. This is intentionally NOT just "any authenticated session":
 * - A fresh top-level visit to debug.sim.localhost never carries ?townsim_portal=1.
 * - A stale cookie with no active swap will fail the BFF validation step (swapKind = NONE).
 * Both guards together prevent the r22 auto-jump regression.
 */
function hasTownsimPortalParam(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('townsim_portal') === '1';
  } catch {
    return false;
  }
}

/**
 * The townsim renderer lives at /town/ (a separate nginx-served static app, NOT an Angular route).
 * On a sim host the canonical entry IS the townsim (notes/demo-day-runbook.md lists
 * http://debug.sim.localhost/town/ as the frictionless townsim URL; the building-click "Open Session"
 * then golden-ticket-logs-in in-place and swaps into the portal). A bare top-level visit to
 * http://debug.sim.localhost/ otherwise lands on the debug persona-picker -> /home (the bank
 * portal), so the owner who opened the host root never sees the townsim. Send a fresh top-level sim
 * visit to /town/ so the host root and the /town/ deep-link both land on the townsim.
 *
 * Guarded so this NEVER fires for:
 *  - the portal iframe (it loads /?townsim_portal=1 and must mount <tb-root>, not bounce to /town/),
 *  - a tab that already deliberately entered the portal (tb-debug-portal-active set -> resume it),
 *  - a visit already under /town/ (the static app, which never boots this Angular shell anyway),
 * and only on sim hosts -- debug.toybank/.demo keep the picker.
 */
function shouldRedirectSimRootToTown(): boolean {
  if (typeof window === 'undefined') return false;
  if (!isSimHost()) return false;
  if (hasTownsimPortalParam()) return false;
  if (hasDebugPortalActiveFlag()) return false;
  const path = window.location.pathname || '/';
  return !path.startsWith('/town');
}

// Gate the portal-resume on a per-tab flag set ONLY when the user deliberately entered the
// portal this tab (survives a window.location reload, but NOT a fresh visit / stale cookie)
// so we never auto-jump into a broken portal and trap the login picker.
const DEBUG_PORTAL_ACTIVE = 'tb-debug-portal-active';
function hasDebugPortalActiveFlag(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  try { return sessionStorage.getItem(DEBUG_PORTAL_ACTIVE) === '1'; } catch { return false; }
}
function setDebugPortalActiveFlag(): void {
  if (typeof sessionStorage === 'undefined') return;
  try { sessionStorage.setItem(DEBUG_PORTAL_ACTIVE, '1'); } catch { /* storage may be unavailable */ }
}
function clearDebugPortalActiveFlag(): void {
  if (typeof sessionStorage === 'undefined') return;
  try { sessionStorage.removeItem(DEBUG_PORTAL_ACTIVE); } catch { /* storage may be unavailable */ }
}

/**
 * Debug wrapper around the customer SPA. Left: actual-user sign-in picker plus a mock phone for
 * later, operation-bound step-up. Centre: login then the real portal ({@code <tb-root>}). Right: a
 * live HTTP log and the bank (domain-event) log. Username selection installs only a real cookie
 * session; it never presses the phone or creates MFA assurance.
 *
 * This wrapper is DEMO-AGNOSTIC. It deals only with ACTUAL IdP users (alice/omar/morgan/rowan)
 * via the debug login flow. In-platform acting-context switching is owned entirely by the portal
 * (<tb-root> / app.component.ts) which manages its own session-swap controls.
 */
@Component({
  selector: 'tb-debug-shell',
  imports: [DatePipe, NgTemplateOutlet, TranslocoPipe, LoginScreenComponent, AppComponent, TestAccountBadgeComponent, TestAccountInfoDialogComponent],
  // Owner UX critique (2026-09-14): the drawers no longer own a header bar of their own -- the
  // toggle is a `manage_accounts` icon in app.component.ts's existing top nav bar, immediately
  // left of the Org dropdown. Providing TB_DEBUG_DRAWERS here (rather than app.component.ts
  // importing this component) keeps the bff build's bundle free of debug-wrapper code -- the
  // nested <tb-root> injects the token optionally and simply renders nothing when it is absent.
  providers: [{ provide: TB_DEBUG_DRAWERS, useExisting: DebugShellComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
      <div class="shell" [class.shell--onboarding]="onboardingRoute()" [class.shell--portal]="phase() === 'portal'"
        [class.shell--ceremony]="ceremonyOpen()" [style.--drawer-progress]="drawerProgress()"
        (keydown.escape)="closeDrawers()" (keydown.alt.shift.d)="focusDrawerHandle($event)">
        <!-- Both drawers remain mounted and share one reveal position. -->
        <aside class="left" id="debug-drawer-left" data-testid="debug-drawer-left"
          aria-label="Debug sign-in and mock phone" [attr.inert]="drawersHidden() ? '' : null"
          [attr.aria-hidden]="drawersHidden() ? 'true' : 'false'">
          <section class="panel" [class.panel--picker]="!signedIn() && sessionState() !== 'checking'">
            <!-- The heading is the PICKER's heading, and two live e2e specs
                 (debug-signed-out-redirect.spec.ts, demo-walkthrough/n0-quest-completion.live.spec.ts)
                 use "sign in as actual user" as their "we got bounced to the picker" detector.
                 It therefore must render if and ONLY if the picker is on screen. -->
            @if (signedIn()) {
              <h2>Debug: signed in</h2>
              <div class="signed-in" data-testid="debug-signed-in">
                <!-- data-testid="session-status" is a contract: web/e2e/lib/demo-harness.mjs
                     (loginViaDebugUser) waits on it as Layer-1 proof the BFF cookie session
                     landed, and debug-wrapper.spec.ts matches /actual user alice/i against it.
                     Keep the "Signed in as actual user <label>" prefix verbatim. -->
                <p class="session-status" data-testid="session-status" [attr.data-actual-username]="actualUsername()">Signed in as actual user {{ actualUserLabel() }}@if (demoPersonaLabel(); as persona) {<span data-testid="session-status-persona"> — acting as {{ persona }}</span>}</p>
                <p class="hint layer-note">Demo personas are switched inside the portal (Switch Demo User).</p>
                @if (actualUsername(); as signedInAs) {
                  <div class="signed-in-sessions" data-testid="debug-signed-in-sessions">
                    <span class="hint">Live in this account (you included):</span>
                    <tb-test-account-badge [username]="signedInAs" [showKinds]="false" [activity]="testAccounts.activityByUsername().get(signedInAs)" />
                    <button class="persona-info" type="button" data-testid="debug-signed-in-info"
                      [attr.aria-label]="'About test account ' + signedInAs" (click)="openAccountInfo(signedInAs)">
                      <span class="material-symbols-outlined" aria-hidden="true">info</span>
                    </button>
                  </div>
                }
                <div class="signed-in-actions">
                  <button class="session-action" type="button" data-testid="debug-switch-user"
                    [disabled]="endingSession()" (click)="switchUser()">Switch user</button>
                  <button class="session-action" type="button" data-testid="debug-sign-out"
                    [disabled]="endingSession()" (click)="signOut()">Sign out</button>
                </div>
                @if (logoutError()) {
                  <p class="hint" role="alert" data-testid="debug-logout-error">{{ 'actions.logoutError' | transloco }}</p>
                }
              </div>
            } @else {
              <h2>Debug: sign in as actual user</h2>
              @if (endingSession()) {
                <p class="hint" data-testid="debug-session-ending">Ending the current session…</p>
              } @else if (sessionState() === 'checking') {
                <!-- Sub-second in practice: one unfenced GET, issued as the first thing
                     ngOnInit does (~60ms against the live stack). The picker is withheld for that
                     window on purpose -- offering "sign in" to someone already signed in is the
                     defect. SESSION_PROBE_TIMEOUT_MS bounds the wait so a hung probe still lands
                     on the picker rather than nothing. -->
                <p class="hint" data-testid="debug-session-checking">Re-checking your session…</p>
              }
              @if (phase() === 'portal') {
                <!-- Unauthenticated /open entry (openAccountPortal()) mounts <tb-root> without
                     a session, which disables every picker control below (they require
                     phase() === 'login'). This is the only way back to a usable picker from
                     that state -- the customer-facing /open flow's own "Sign in" (A3) still
                     works normally since it's a top-level redirect, not this picker. -->
                <button
                  type="button"
                  class="session-action"
                  data-testid="debug-back-to-sign-in"
                  (click)="backToSignIn()"
                >Back to sign-in</button>
              }
            }

            <!-- Portal picker: 2x2 grid (Portal|Backoffice x Normal|Demo) + a 5th, extra-wide
                 Simulator button spanning beneath it (owner UX request). Icons + presentation
                 come from LOGIN_PRESENTATION (login/login-screen.component.ts), keyed by the
                 truthful label -- the same shared branding source the login screen itself uses. -->
            @if (!signedIn() && sessionState() !== 'checking') {
            <section class="picker-section" aria-labelledby="portal-picker-heading">
              <h3 id="portal-picker-heading" class="picker-heading">Select target portal</h3>
              <div class="target-grid" aria-label="Debug login target">
                @for (targetOption of targetOptions; track targetOption.id) {
                  <button
                    class="target"
                    type="button"
                    [attr.data-testid]="'debug-target-' + targetOption.id"
                    [class.active]="target() === targetOption.id"
                    [disabled]="phase() !== 'login' || endingSession() || loggingIn()"
                    (click)="selectTarget(targetOption.id)"
                  >
                    <span class="material-symbols-outlined target-icon" aria-hidden="true">{{ presentationFor(targetOption.label).icon }}</span>
                    <span class="target-label">{{ targetOption.label }}</span>
                  </button>
                }
                <button
                  class="target target--wide target--simulator"
                  type="button"
                  [attr.data-testid]="'debug-target-' + simulatorTarget.id"
                  [class.active]="target() === simulatorTarget.id"
                  [disabled]="phase() !== 'login' || endingSession() || loggingIn()"
                  (click)="selectTarget(simulatorTarget.id)"
                >
                  <!-- Owner icon pass 2026-08-19: snowflake (Material Symbols ligature ac_unit —
                       the Bank of Antarctica mark) leads, then the presentation's city icon;
                       row-wrapped because .target stacks its children in a column. -->
                  <span class="target-icon-row" aria-hidden="true">
                    <span class="material-symbols-outlined target-icon">ac_unit</span>
                    <span class="material-symbols-outlined target-icon">{{ presentationFor(simulatorTarget.label).icon }}</span>
                  </span>
                  <span class="target-label">{{ simulatorTarget.label }}</span>
                </button>

                <!-- Owner UX request (KYC front-door overnight lane, 2026-09-23): NOT a login
                     target -- clicking mounts the real portal unauthenticated and routes it to
                     /open, same drawers-open pattern as the rest of this picker. See
                     openAccountPortal()'s docstring for why it does not touch sessionState/the
                     tb-debug-portal-active flag. -->
                <button
                  class="target target--wide target--open-account"
                  type="button"
                  data-testid="debug-open-account"
                  [disabled]="phase() !== 'login' || endingSession() || loggingIn()"
                  (click)="openAccountPortal()"
                >
                  <span class="material-symbols-outlined target-icon" aria-hidden="true">note_add</span>
                  <span class="target-label">Open an account</span>
                </button>
              </div>
            </section>

            <!-- User picker: populated dynamically from PersonaService's roster fetch
                 (/bff/debug/users), kicked off as the first thing ngOnInit does. -->
            <section class="picker-section picker-section--users" aria-labelledby="user-picker-heading">
              <h3 id="user-picker-heading" class="picker-heading">Select actual sign-in user</h3>
              <!-- The two identity layers, named where the confusion happens. 18/18 exploratory
                   runs on 2026-09-12 reported some form of "I picked Alice but the app says
                   Persephone": this list is the ACTUAL IdP login (CLAUDE.md layer 1); the mythic
                   names in the portal header are in-platform PERSONAS (layer 2), an acting
                   context put on top of that login and never a login of their own. -->
              <!-- One line on purpose: every line here is a line the account list does not get.
                   The signed-in face repeats that personas are switched inside the portal. -->
              <p class="picker-note" data-testid="debug-user-picker-note">IdP test accounts, not demo personas.</p>
              @if (personas.loadState() === 'loading' || testAccounts.loadState() === 'loading') {
                <p role="status">Loading current test accounts…</p>
              } @else if (personas.loadState() === 'error' || testAccounts.loadState() === 'error') {
                <p role="alert" data-testid="debug-roster-unavailable">Current test accounts are unavailable. Previous account facts are informational; sign-in choices will return after a fresh read.</p>
                <button type="button" (click)="personas.loadPersonas()">Retry current sign-in choices</button>
              }
              <!-- debug-wrapper v2 (notes/debug-wrapper-v2.md): the account list scrolls on its own
                   so the heading, the picker note and the "more accounts" line stay put. Each
                   actual user carries a live session indicator (awareness only -- sharing a test
                   account is normal) and an info button. Accounts recently used on this host move
                   into the top group (never duplicated: persona-<id> testids stay unique).
                   Keyboard: the list is ONE radio group and one tab stop (roving tabindex). Arrow
                   keys move and select, Home/End jump, Enter on the selected row signs in, and Tab
                   reaches the selected row's info button. -->
              <div class="account-scroll" data-testid="debug-account-list" role="radiogroup"
                aria-label="Actual sign-in users" (keydown)="onAccountListKeydown($event)">
                @if (pickerGroups().recent.length) {
                  <h4 class="account-group" data-testid="debug-recent-accounts-heading">Recently used</h4>
                  <div class="persona-list" data-testid="debug-recent-accounts">
                    @for (p of pickerGroups().recent; track p.id) {
                      <ng-container [ngTemplateOutlet]="accountRow" [ngTemplateOutletContext]="{ $implicit: p }" />
                    }
                  </div>
                }
                @if (pickerGroups().customers.length) {
                  <h4 class="account-group">Customers</h4>
                  <div class="persona-list" data-testid="debug-customer-accounts">
                    @for (p of pickerGroups().customers; track p.id) {
                      <ng-container [ngTemplateOutlet]="accountRow" [ngTemplateOutletContext]="{ $implicit: p }" />
                    }
                  </div>
                }
                @if (pickerGroups().staff.length) {
                  <h4 class="account-group">Bank staff</h4>
                  <div class="persona-list" data-testid="debug-staff-accounts">
                    @for (p of pickerGroups().staff; track p.id) {
                      <ng-container [ngTemplateOutlet]="accountRow" [ngTemplateOutletContext]="{ $implicit: p }" />
                    }
                  </div>
                }
                @if (pickerGroups().other.length) {
                  <!-- Only when neither the roster nor /bff/debug/users knows an audience: never
                       guessed into Customers or Bank staff. -->
                  <h4 class="account-group">Other accounts</h4>
                  <div class="persona-list" data-testid="debug-other-accounts">
                    @for (p of pickerGroups().other; track p.id) {
                      <ng-container [ngTemplateOutlet]="accountRow" [ngTemplateOutletContext]="{ $implicit: p }" />
                    }
                  </div>
                }
              </div>
              <ng-template #accountRow let-p>
                <div class="persona-row" [class.persona-row--active]="active()?.id === p.id"
                  [attr.data-account-row]="p.username">
                  <!-- The whole row is this button's hit area (.persona::after); the pill passes
                       clicks through and only the info button sits above it. -->
                  <button class="persona" type="button" role="radio"
                    [attr.data-testid]="'persona-' + p.id"
                    [attr.data-account-radio]="p.id"
                    [class.active]="active()?.id === p.id"
                    [attr.aria-checked]="active()?.id === p.id ? 'true' : 'false'"
                    [attr.tabindex]="accountTabStop() === p.id ? 0 : -1"
                    [disabled]="phase() !== 'login' || endingSession() || loggingIn()"
                    (click)="selectPersona(p)"
                    (keydown.enter)="onAccountEnter($event, p)">
                    <span class="persona-name">{{ accountLabel(p.label).name }}</span>
                    @if (accountLabel(p.label).detail; as detail) {
                      <span class="persona-detail">{{ detail }}</span>
                    }
                  </button>
                  <span class="persona-tools">
                    <tb-test-account-badge part="pill" [username]="p.username" [activity]="testAccounts.activityByUsername().get(p.username)" />
                    <button class="persona-info" type="button"
                      [attr.data-testid]="'persona-info-' + p.id"
                      [attr.aria-label]="'About test account ' + p.username"
                      [attr.tabindex]="accountTabStop() === p.id ? 0 : -1"
                      (click)="openAccountInfo(p.username)">
                      <span class="material-symbols-outlined" aria-hidden="true">info</span>
                    </button>
                  </span>
                  @if (isLive(p.username)) {
                    <div class="persona-meta">
                      <tb-test-account-badge part="kinds" [username]="p.username" [activity]="testAccounts.activityByUsername().get(p.username)" />
                    </div>
                  }
                  @if (accountMetadata(p.username); as metadata) {
                    @if (curatorLine(metadata) || metadata.linkedPartnerApps?.length) {
                    <div class="persona-meta">
                      @if (curatorLine(metadata); as line) { <span [attr.data-testid]="'account-curator-' + p.username">{{ line }}</span> }
                      @for (app of metadata.linkedPartnerApps ?? []; track app) { <span class="lifecycle" [title]="'Desired link to ' + app + '; live partner facts unavailable'">{{ app }} · linked</span> }
                    </div>
                    }
                  }
                </div>
              </ng-template>
              @if (testAccounts.notYetSignInable().length) {
                <details class="more-accounts" data-testid="debug-more-accounts">
                  <summary>More test accounts ({{ testAccounts.notYetSignInable().length }} unavailable for sign-in{{ testAccounts.loadState() === 'error' ? '; previous snapshot' : '' }})</summary>
                  <ul>
                    @for (a of testAccounts.notYetSignInable(); track a.username) {
                      <li>
                        <button type="button" class="link" [attr.data-testid]="'account-info-' + a.username"
                          (click)="openAccountInfo(a.username)">{{ a.displayName }}</button>
                        <span class="lifecycle">{{ a.lifecycle }}{{ a.blockedReason ? ' · ' + a.blockedReason.text : '' }}</span>
                        @if (curatorLine(a); as line) { <span [attr.data-testid]="'account-curator-' + a.username">{{ line }}</span> }
                        @for (app of a.linkedPartnerApps ?? []; track app) { <span class="lifecycle">{{ app }} · linked (live facts unavailable)</span> }
                      </li>
                    }
                  </ul>
                </details>
              }
            </section>
            }
          </section>

          <section class="panel phone" tabindex="0" aria-label="Mock phone messages">
            <h2>Mock phone</h2>
            <p class="hint">Operation-bound test codes. Receiving one does not approve it; tap the message to act. Newest first.</p>
            @if (phone.messages().length === 0) {
              <p class="empty">No messages.</p>
            } @else {
              @for (m of phone.messages(); track m.id) {
                <button
                  class="sms"
                  type="button"
                  data-testid="phone-sms"
                  [class.sms--approve]="m.kind === 'APPROVE'"
                  [class.sms--consumed]="m.consumed"
                  [disabled]="m.consumed"
                  [attr.aria-label]="phoneMessageAriaLabel(m)"
                  (click)="phone.tap(m.code, m.id)">
                  <span class="kind-badge" [class.kind-badge--approve]="m.kind === 'APPROVE'"
                    data-testid="phone-sms-kind">
                    {{ m.kind === 'APPROVE' ? 'Tap to approve' : 'Enter this code' }}
                  </span>
                  <span class="from">{{ m.from }}</span>
                  <span class="ts">{{ m.at | date:'HH:mm:ss' }}</span>
                  @if (m.kind !== 'APPROVE') {
                    <span class="code">{{ m.code }}</span>
                  }
                  @if (m.operation) {
                    <span class="operation" data-testid="phone-sms-operation">{{ m.operation }}</span>
                  }
                  <span class="note">{{ m.note }}</span>
                  @if (m.consumed) {
                    <span class="consumed-badge" data-testid="phone-sms-consumed">Used</span>
                  }
                </button>
              }
            }
          </section>
        </aside>

        <!-- CENTRE: modeled login → real portal; operation step-up stays in the fake phone.
             In portal phase the center becomes the outer frame for <tb-root>:
             transform: translate(0) creates a new containing block so the portal's
             position:fixed nav rail stays clipped to this column rather than escaping
             to the viewport edge and overlapping the wrapper panels. -->
        <div class="center" [attr.role]="onboardingRoute() ? null : 'main'" [class.center--portal]="phase() === 'portal'">
          @switch (onboardingRoute() ? 'portal' : phase()) {
            @case ('login') {
              <!-- SAME shared login screen the real portal renders (app.component.ts) --
                   <tb-login-screen> (login/login-screen.component.ts). Only the submit
                   strategy differs (POST /bff/debug/login vs redirect to Keycloak) plus a
                   handful of surface-local presentation inputs (label/testid text, and
                   disabled/loading/hint/error sourced from THIS surface's own login-attempt
                   state, since a debug login is a real HTTP call the real portal's plain
                   redirect never makes). Owner ruling 2026-08-18: the debug wrapper must not
                   have its own alternate login page. Branding is parameterized by
                   presentation() (title/subtitle/icon/accent/wordmark) -- config-driven per
                   target, not 5 copy-pasted components. -->
              <tb-login-screen
                [presentation]="presentation()"
                [tagline]="presentation().subtitle"
                [usernameControl]="loginForm.controls.username"
                actionLabel="Continue"
                [disabled]="!active() || !pickerOrder().some(isSelectedAccount) || loggingIn() || endingSession() || sessionState() === 'checking'"
                [loading]="loggingIn()"
                [hint]="!active() ? '← choose an actual user to begin.' : ''"
                [error]="loginError()"
                [strategy]="loginSubmitStrategy"
                [notice]="loginNotice()"
              />
            }
            @case ('portal') {
              <!-- The entire portal (left nav + top bar + content) mounts here.
                   center--portal adds transform:translate(0) so the portal's fixed-position
                   nav drawer is contained within this column rather than bleeding out to the
                   viewport left edge. The portal's min-height:100dvh becomes relative to this
                   containing block, so its bar sticky + body scroll work inside the column. -->
              <tb-root />
            }
          }
        </div>

        <!-- RIGHT: HTTP log + bank event log -->
        <aside class="right" id="debug-drawer-right" data-testid="debug-drawer-right"
          aria-label="Debug logs" [attr.inert]="drawersHidden() ? '' : null"
          [attr.aria-hidden]="drawersHidden() ? 'true' : 'false'">
          <section class="panel log" aria-label="HTTP request log">
            <h2>HTTP</h2>
            <div class="log-body" role="region" tabindex="0" aria-label="HTTP request entries">
              @for (e of httpLog.entries(); track e.at) {
                <div class="row" [class.bad]="e.error || e.status >= 400">
                  <span class="m">{{ e.method }}</span>
                  <span class="u">{{ e.url }}</span>
                  <span class="s">{{ e.status }}</span>
                  <span class="t">{{ e.ms }}ms</span>
                </div>
              }
            </div>
          </section>
          <section class="panel log" aria-label="Bank event log">
            <h2>Bank events</h2>
            <div class="log-body" role="region" tabindex="0" aria-label="Bank event entries">
              @for (ev of bankFeed.events(); track ev.id) {
                <div class="row"><span class="ty">{{ ev.type }}</span><span class="su">{{ ev.summary }}</span></div>
              }
            </div>
          </section>
        </aside>
      </div>
      <!-- Outside both drawers so a closed (inert) drawer can never make the open dialog inert. -->
      @if (accountInfoFor(); as infoUsername) {
        <tb-test-account-info-dialog [username]="infoUsername" (closed)="accountInfoFor.set(null)" />
      }
  `,
  styles: `
    /* -- Account list (debug-wrapper v2) ------------------------------------------------------
       One row per actual user: name over "org (role)" on the left, the live pill and the info
       button on the right, the session-kind breakdown under it only when the account is live. */
    /* The users section takes the drawer height the targets leave. The list shrinks to fit, down
       to its min-height (a group header plus about three idle rows); below that the panel
       scrolls. Measured at 1280x720, 1366x768, 1280x800 and 1440x900 (notes/debug-wrapper-v2.md
       section 1.1). */
    .picker-section--users { display: flex; flex-direction: column; flex: 0 1 auto; min-height: 0; }
    .picker-section--users > :not(.account-scroll) { flex-shrink: 0; }
    .account-scroll {
      --account-group-height: 2rem;
      flex: 0 1 auto; min-height: 14rem; overflow-y: auto; overscroll-behavior: contain;
      margin: 0 calc(-1 * var(--tb-space-1, 4px)); padding: 0 var(--tb-space-1, 4px) var(--tb-space-1, 4px);
      border-top: 1px solid var(--tb-border-line, #ddd); border-bottom: 1px solid var(--tb-border-line, #ddd);
      scrollbar-gutter: stable;
      /* Focus scrolling (Shift+Tab, arrow keys) keeps a row clear of the sticky group header. */
      scroll-padding-top: var(--account-group-height);
    }
    .account-group {
      position: sticky; top: 0; z-index: 2; box-sizing: border-box; min-height: var(--account-group-height);
      margin: 0; padding: var(--tb-space-2, 8px) var(--tb-space-1, 4px) var(--tb-space-1, 4px);
      font: var(--tb-type-body-small); font-weight: 600; letter-spacing: 0.02em; color: var(--tb-text-muted, #666);
      background: var(--tb-surface-inset, #f4f5f7);
    }
    .persona-list { display: flex; flex-direction: column; gap: var(--tb-space-1, 4px); }
    /* The button spans the row (name, then "org (role)" at full width); the pill and the info
       button sit over its top-right corner, beside the name line only. The button's ::after
       covers the whole row, so there is no dead strip and the pill never swallows a click. */
    .persona-row {
      position: relative; display: flex; flex-direction: column;
      padding: 2px; border: 1px solid var(--tb-border-line, #ccc); border-radius: var(--tb-radius-sm, 6px);
      background: var(--tb-surface, #fff);
    }
    .persona-row:hover { border-color: color-mix(in srgb, var(--tb-brand-accent, #3358ff) 50%, var(--tb-border-line, #ccc)); }
    .persona-row:has(.persona:focus-visible) { outline: 2px solid var(--tb-brand-accent, #4f86f7); outline-offset: 1px; }
    .persona-row--active {
      border-color: var(--tb-brand-accent, #3358ff);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--tb-brand-accent, #3358ff) 26%, transparent);
      background: color-mix(in srgb, var(--tb-brand-accent, #3358ff) 6%, var(--tb-surface, #fff));
    }
    .persona::after { content: ''; position: absolute; inset: 0; border-radius: inherit; }
    .persona-tools {
      position: absolute; top: 4px; right: 4px; z-index: 1; display: inline-flex; align-items: center; gap: 2px;
      pointer-events: none;
    }
    .persona-tools .persona-info { pointer-events: auto; }
    .persona-row .persona-meta { padding: 0 var(--tb-space-2, 8px) var(--tb-space-1, 4px); }
    .persona-info {
      display: inline-grid; place-items: center; width: 28px; height: 28px;
      border: 1px solid transparent; border-radius: var(--tb-radius-sm, 6px);
      background: transparent; color: var(--tb-text-muted, #555); cursor: pointer;
    }
    .persona-info .material-symbols-outlined { font-size: 1.1rem; }
    .persona-info:hover { color: var(--tb-brand-accent, #3358ff); border-color: var(--tb-brand-accent, #3358ff); }
    .persona-info:focus-visible, .more-accounts .link:focus-visible { outline: 2px solid var(--tb-brand-accent, #4f86f7); outline-offset: 1px; }
    .signed-in-sessions { display: flex; flex-wrap: wrap; align-items: center; gap: var(--tb-space-1, 4px); margin: 0 0 var(--tb-space-2, 8px); }
    .signed-in-sessions .persona-info { border-color: var(--tb-border-line, #ccc); background: var(--tb-surface, #fff); }
    .more-accounts { margin-top: var(--tb-space-2, 8px); font: var(--tb-type-body-small); color: var(--tb-text-muted, #666); }
    .more-accounts summary { cursor: pointer; min-height: 24px; }
    .more-accounts ul { list-style: none; margin: var(--tb-space-1, 4px) 0 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
    .more-accounts li { display: flex; align-items: baseline; justify-content: space-between; gap: var(--tb-space-2, 8px); }
    .more-accounts .link { background: none; border: 0; padding: 2px 0; min-height: 24px; color: var(--tb-brand-accent, #3358ff); cursor: pointer; font: inherit; text-align: left; }
    .more-accounts .link:hover { text-decoration: underline; }
    .more-accounts .lifecycle { flex: none; white-space: nowrap; }
    .shell.shell--onboarding { display: block; height: auto; min-height: 100dvh; padding: 0; }
    .shell--onboarding > aside, .shell--onboarding .drawer-handle { display: none !important; }
    .shell.shell--onboarding > .center { width: 100%; max-width: none; padding: 0; transform: none; overflow: visible; border: 0; border-radius: 0; }

    /* -- Outer wrapper grid ------------------------------------------------------ */
    .shell {
      position: relative;
      display: grid;
      grid-template-columns: 270px minmax(0, 1fr) 340px;
      height: 100dvh;
      background: var(--tb-surface-inset, #f4f5f7);
    }

    /* -- Side panels ------------------------------------------------------------ */
    .left, .right { display: flex; flex-direction: column; min-width: 0; overflow: hidden; border-color: var(--tb-border-line, #ddd); }
    .left { border-right: 1px solid var(--tb-border-line, #ddd); }
    .right { border-left: 1px solid var(--tb-border-line, #ddd); }
    .panel { padding: 0.85rem; overflow: auto; }
    .panel.log { display: flex; flex-direction: column; min-height: 0; overflow: hidden; }
    .left .phone, .right .log:last-child { flex: 1; border-top: 1px solid var(--tb-border-line, #ddd); }
    .right .log:first-child { flex: 1; min-height: 0; }

    /* -- Center column: login/MFA mode (padded, scrollable) ------------------- */
    .center { min-width: 0; overflow: auto; padding: 1.25rem; position: relative; }

    /* -- Center column: portal mode ---------------------------------------------
       transform: translate(0) promotes .center to a new containing block so the
       portal's position:fixed nav rail (z-index 90) and top bar (z-index 100) are
       clipped to this column rather than escaping to the viewport edge and
       overlapping the wrapper's left/right panels.
       overflow:hidden prevents the portal's min-height:100dvh from creating scroll
       on .center itself; the portal's internal body scroll still works because the
       content-wrap inside <tb-root> is flex:1 and scoped within its shell grid.    */
    .center--portal {
      padding: 0;
      overflow: hidden;
      transform: translate(0);
      /* Override portal CSS custom properties so its fixed rail and body offset
         match the column dimensions rather than the full viewport. */
      --tb-app-bar-height: 48px;
      --tb-nav-rail-width: 212px;
      --tb-app-viewport-height: 100%;
    }
    /* The portal's tb-root host must fill the center column. */
    .center--portal tb-root { display: block; height: 100%; }

    /* .center--portal is its OWN stacking context (promoted by transform above), so anything the
       embedded portal renders — including a fixed, [aria-modal="true"] dialog like the persona
       landing modal or the step-up ceremony — only ever competes in z-index against OTHER content
       inside that same context. It cannot out-rank a SIBLING of .center--portal (namely the
       .left/.right debug drawers below, z-index: var(--tb-z-overlay)) no matter how high its own
       z-index is set inside the portal: that comparison is decided by .center--portal's OWN
       z-index (normally "auto", which loses to the drawers' explicit z-index). Raise it to modal
       tier ONLY while a modal is actually open inside the portal, so the drawer keeps its normal
       "slides over the portal" behavior the rest of the time (xt run
       20260913-075852-limits-and-velocity-b39e: the persona-landing Confirm button was obscured by
       the debug drawer, forcing the drawer closed first to reach it). */
    .center--portal:has([aria-modal="true"]) { z-index: var(--tb-z-modal, 400); }

    /* ...but the step-up ceremony is the ONE portal modal whose completion REQUIRES a control
       that lives outside the portal: the mock phone in the left drawer. The rule immediately
       above raised the whole portal column to modal tier, and the ceremony dialog's backdrop is
       position:fixed inset:0 inside that column -- so it painted over the drawers and swallowed
       every pointer event aimed at the phone. The drawer was visible (the ceremony effect in the
       class auto-opens it for a new challenge) and completely untappable: four xt runs on
       2026-09-14 stalled here, and run 20260914-065907-payment-order-approvals-0eba only got past
       it by dispatching a scripted JS click straight at the message element.

       So while a ceremony is pending, the LEFT drawer -- and only the left drawer -- is lifted
       back above the modal tier. The right drawer (logs) stays put deliberately: it is read-only,
       and at viewports just over the 1100px breakpoint its 340px column would overlap the centred
       460px dialog panel and hide the dialog's own buttons.

       Keyboard reachability needs no exemption: the CDK focus trap only cycles Tab, it does not
       block clicks or programmatic focus, and the Alt+Shift+D binding lives on .shell -- an
       ancestor of the trapped dialog -- so it still reaches the drawer handle from inside the
       ceremony, and Tab from the handle walks into the phone in DOM order.

       Carve-out at 640px and below: there the drawers are full-width halves rather than side
       columns, so lifting the left one would cover the dialog's "Continue to verify" button --
       the step BEFORE the phone is even in play. Handsets keep today's stacking. */
    @media (min-width: 641px) {
      .shell--ceremony.shell--portal .left { z-index: calc(var(--tb-z-modal, 400) + 2); }
    }

    /* Tablet debug workbench: keep the actual-user picker + fake MFA phone visible,
       give the portal a real interaction column, and drop only the secondary telemetry.
       The former fixed 270 + 340 sidebars left 158px at the 768px Playwright viewport,
       causing the left phone panel to cover portal modal controls. */
    @media (max-width: 1100px) {
      .shell { grid-template-columns: 250px minmax(0, 1fr); }
      .right { display: none; }
    }

    /* On phone-sized debug screens the fake phone remains available during an operation-bound
       ceremony. The user picker is hidden after login, leaving only the device above the portal. */
    @media (max-width: 640px) {
      .shell {
        grid-template-columns: minmax(0, 1fr);
        grid-template-rows: minmax(0, auto) minmax(0, 1fr);
      }
      .left {
        max-height: 45vh;
        border-right: 0;
        border-bottom: 1px solid var(--tb-border-line, #ddd);
      }
    }

    /* Owner UX critique (2026-09-14): the drawers used to reveal below a dedicated 44px toggle
       bar (--debug-toolbar-height) that ran the full width above both drawers AND the portal.
       That bar is gone -- the toggle is now the top nav bar's manage_accounts icon (see
       TB_DEBUG_DRAWERS) -- so the portal gets the full shell height and the drawers reveal
       edge-to-edge (top: 0) instead of starting below a strip that no longer exists.

       grid-template-rows is reset here too (not just grid-template-columns): at <=640px the
       earlier ".shell" mobile rule above sets a TWO-row template (an "auto" row + a "1fr" row)
       for the non-portal login/MFA phase, where ".left" (the mock phone) is a normal grid-flow
       item stacked above the content. In portal mode ".left"/".right" are pulled out of flow via
       position:absolute (below), so ".center" is left as the ONLY flowed grid item and, having no
       explicit grid-row, auto-places into that first "auto" track -- sizing to its OWN content
       height instead of the full viewport. Concretely this collapsed the embedded portal (and
       everything position:fixed inside it, e.g. <tb-mobile-nav>'s backdrop, whose containing
       block is this transformed ancestor) to ~309px tall on a 375x812 mobile viewport, leaving
       the bottom ~500px painting the bare .shell--portal background -- reproduced by
       specs/mobile/mobile-nav.spec.ts "backdrop click closes the drawer" tapping the exposed
       strip beside the drawer at y=400 and hitting the shell instead of the backdrop. */
    .shell--portal {
      grid-template-columns: minmax(0, 1fr);
      grid-template-rows: minmax(0, 1fr);
      overflow: hidden;
    }
    .shell--portal .center { grid-column: 1; grid-row: 1; min-height: 0; }
    .shell--portal .left, .shell--portal .right {
      position: absolute; top: 0; bottom: 0;
      display: flex; max-height: none; box-sizing: border-box;
      z-index: var(--tb-z-overlay, 300);
      background: var(--tb-surface-inset, #f4f5f7);
      transition: transform var(--tb-motion-base, 200ms) var(--tb-motion-easing, ease);
    }
    .shell--portal .left > .panel:first-child {
      max-height: 55%; flex-shrink: 0; box-sizing: border-box;
    }
    .shell--portal .left {
      left: 0; width: min(270px, 50%);
      transform: translateX(calc((var(--drawer-progress) - 1) * 100%));
    }
    .shell--portal .right {
      right: 0; width: min(340px, 50%);
      transform: translateX(calc((1 - var(--drawer-progress)) * 100%));
    }
    .shell--portal aside[inert] { pointer-events: none; }
    /* Handsets use full-width half-height drawers so phone messages and logs stay readable.
       Both still slide to opposite sides and share the same toggle and state. */
    @media (max-width: 640px) {
      .shell--portal .left, .shell--portal .right { width: 100%; }
      .shell--portal .left { bottom: 50%; }
      .shell--portal .right { top: 50%; border-top: 1px solid var(--tb-border-line, #ddd); }
      .shell--portal .left .panel:first-child { display: none; }
    }
    @media (prefers-reduced-motion: reduce) {
      .shell--portal .left, .shell--portal .right { transition: none; }
    }

    /* -- Typography / common --------------------------------------------------- */
    h2 { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--tb-text-muted, #666); margin: 0 0 0.5rem; }
    .hint, .empty { font-size: 0.78rem; color: var(--tb-text-muted, #777); }
    .session-status { font-size: 0.78rem; color: var(--tb-text-primary, #333); font-weight: 600; margin: 0 0 0.5rem; }

    /* -- Signed-in face (replaces the picker while a live BFF session is known) --------------- */
    .signed-in { display: flex; flex-direction: column; gap: 0.35rem; }
    .signed-in .layer-note { margin: 0; line-height: 1.35; }
    .signed-in-actions { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.35rem; }
    .session-action {
      flex: 1 1 auto; min-height: 32px; padding: 0.35rem 0.6rem;
      border: 1px solid var(--tb-border-line, #ccc); border-radius: var(--tb-radius-sm, 6px);
      background: var(--tb-surface, #fff); color: var(--tb-text-primary, #333);
      font: inherit; font-size: 0.78rem; cursor: pointer;
    }
    .session-action:hover:not(:disabled) { border-color: var(--tb-brand-accent, #3358ff); }
    .session-action:focus-visible { outline: 2px solid var(--tb-brand-accent, #4f86f7); outline-offset: 1px; }
    .session-action:disabled { opacity: 0.5; cursor: not-allowed; }

    /* -- Left panel: two labeled picker sections ("Select target portal" / "Select user") ---- */
    .picker-section { margin: var(--tb-space-3, 12px) 0; }
    .picker-section:first-of-type { margin-top: var(--tb-space-2, 8px); }
    .picker-heading { font: var(--tb-type-body-small); font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--tb-text-muted, #777); margin: 0 0 var(--tb-space-2, 8px); }
    .picker-note { font: var(--tb-type-body-small); color: var(--tb-text-muted, #777); margin: 0 0 var(--tb-space-2, 8px); }
    /* While the picker shows, the first panel is a column so the account list can take the
       height that is left; the panel itself still scrolls if even the minimum does not fit. */
    .panel--picker { display: flex; flex-direction: column; min-height: 0; flex: 1 1 auto; }
    .panel--picker > * { flex-shrink: 0; }
    .panel--picker > .picker-section--users { flex-shrink: 1; }
    /* The mock phone only ever receives an operation-bound challenge after sign-in. While the
       picker shows it would only take room from the account list, at every width. */
    .left:has(.panel--picker) .phone { display: none; }

    /* -- Portal picker: 2x2 grid (Portal|Backoffice x Normal|Demo, owner UX request) + a 5th,
       extra-wide "Simulator" button spanning beneath it (TownSim debug lane). Cells are near-
       square with a centered Material Symbols icon (DESIGN.md 7.9) above the label. */
    .target-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.4rem; }
    .target {
      display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.3rem;
      width: 100%; aspect-ratio: 1 / 0.62; text-align: center; padding: var(--tb-space-2, 8px) var(--tb-space-1, 4px);
      border: 1px solid var(--tb-border-line, #ccc); border-radius: var(--tb-radius-sm, 6px);
      background: color-mix(in srgb, var(--tb-surface, #fff) 92%, var(--tb-brand-accent, #4f86f7));
      cursor: pointer;
    }
    .target.active { border-color: var(--tb-brand-accent, #4f86f7); box-shadow: 0 0 0 2px color-mix(in srgb, var(--tb-brand-accent, #4f86f7) 26%, transparent); }
    .target:disabled { opacity: 0.6; cursor: not-allowed; }
    /* Icon substantially bigger (owner UX request) so it visually dominates the near-square
       button -- label stays a secondary caption beneath. Button footprint (aspect-ratio-driven,
       not icon-driven) and its comfortable >=44px touch target (DESIGN.md 8) are unchanged. */
    .target-icon { font-size: 2.4rem; color: var(--tb-brand-accent, #4f86f7); }
    /* Simulator's snowflake+city icon pair sits in one row inside the column-flex button. */
    .target-icon-row { display: inline-flex; align-items: center; gap: 0.35rem; }
    .target-label { display: block; font-size: 0.72rem; font-weight: 700; color: var(--tb-text-primary, #333); line-height: 1.2; }
    /* 5th button: extra-wide, spans both grid columns, row layout (icon beside label). */
    .target--wide { grid-column: 1 / -1; flex-direction: row; gap: 0.6rem; aspect-ratio: auto; padding: 0.55rem 0.75rem; }
    .target--simulator .target-icon { color: var(--tb-ai-accent, #7C3AED); }
    .target--simulator.active { border-color: var(--tb-ai-accent, #7C3AED); box-shadow: 0 0 0 2px color-mix(in srgb, var(--tb-ai-accent, #7C3AED) 26%, transparent); }
    /* Laptop heights and handsets: compact target buttons (icon beside label, 44px touch target)
       so the account list gets the room. The tall tiles stay on screens taller than 900px.
       Declared after the base .target rules so it wins. */
    @media (max-height: 900px), (max-width: 640px) {
      .target { flex-direction: row; justify-content: flex-start; aspect-ratio: auto; min-height: 44px; gap: var(--tb-space-2, 8px); padding: var(--tb-space-1, 4px) var(--tb-space-2, 8px); }
      .target-icon { font-size: 1.4rem; }
      .target-label { text-align: left; }
      .target--wide { grid-column: auto; padding: var(--tb-space-1, 4px) var(--tb-space-2, 8px); gap: var(--tb-space-2, 8px); }
      .target-icon-row { gap: 0.15rem; }
    }
    /* Handsets: three compact columns, and less above the list. The drawer keeps the 45vh cap
       (or the room the login card leaves, if that is more), so the Continue button stays on
       screen; the list keeps a group header plus one live row. */
    @media (max-width: 640px) {
      .target-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .target { gap: var(--tb-space-1, 4px); padding: var(--tb-space-1, 4px); }
      .target-label { font-size: 0.68rem; }
      .panel--picker { padding: var(--tb-space-2, 8px) var(--tb-space-3, 12px); }
      .panel--picker > h2 { margin-bottom: var(--tb-space-1, 4px); }
      /* The section headings stay for screen readers; on a handset the drawer heading and the
         buttons say the same, and the 44px they cost is most of a row. */
      .panel--picker .picker-heading {
        position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden;
        clip: rect(0 0 0 0); white-space: nowrap; border: 0;
      }
      .panel--picker .picker-section { position: relative; margin: var(--tb-space-2, 8px) 0 0; }
      .panel--picker .picker-section:first-of-type { margin-top: 0; }
      .panel--picker .picker-note { margin-bottom: var(--tb-space-1, 4px); }
      .account-scroll { min-height: 9.5rem; }
    }

    /* -- Persona list (dynamically populated from PersonaService's roster fetch) ------------- */
    .persona-list { display: flex; flex-direction: column; }
    /* color explicit (2026-09-20 light-mode visibility fix): a native button with no foreground
       color falls back to a UA default keyed off color-scheme, which -- before this fix -- could
       disagree with this button's OWN (correctly theme-resolved) --tb-surface background during
       the debug wrapper's pre-login picker phase (ThemeService, the only thing that ever set
       color-scheme explicitly, is not constructed until AFTER a user is chosen -- see
       styles.scss's now-added prefers-color-scheme fallback for the root cause). Declaring the
       token directly removes the dependency on that UA fallback entirely. */
    .persona {
      display: flex; flex-direction: column; align-items: flex-start; gap: 1px; min-width: 0; min-height: 36px;
      width: 100%; margin: 0; padding: var(--tb-space-1, 4px) var(--tb-space-2, 8px); text-align: left;
      border: 0; border-radius: var(--tb-radius-sm, 6px); background: transparent; color: var(--tb-text-primary, #333); cursor: pointer;
    }
    /* The focus ring is drawn on the whole row (.persona-row:has(.persona:focus-visible)). */
    .persona:focus-visible { outline: none; }
    .persona-name { font: var(--tb-type-label-large); color: var(--tb-text-primary, #333); padding-right: 76px; min-height: 22px; display: flex; align-items: center; }
    .persona-detail {
      font: var(--tb-type-body-small); color: var(--tb-text-muted, #666); max-width: 100%; overflow: hidden;
      display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; line-clamp: 2;
    }
    .persona:disabled { opacity: 0.45; cursor: not-allowed; }
    .notok { color: var(--tb-finance-negative, #c00); margin-left: var(--tb-space-1, 4px); }

    /* -- SMS widget -- 3-col grid: [from + note] [timestamp] [code] ------------- */
    .sms { display: grid; grid-template-columns: 1fr auto auto; grid-template-rows: auto auto auto; gap: 0.1rem 0.5rem; width: 100%; text-align: left; margin: 0.35rem 0; padding: 0.5rem 0.6rem; border: 1px solid var(--tb-border-line, #ccc); border-radius: 10px; background: var(--tb-surface, #fff); cursor: pointer; }
    .sms .kind-badge { grid-column: 1 / 3; grid-row: 1; justify-self: start; font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; padding: 0.1rem 0.4rem; border-radius: 999px; color: var(--tb-brand-accent, #4f86f7); background: color-mix(in srgb, var(--tb-brand-accent, #4f86f7) 14%, transparent); }
    .sms .kind-badge--approve { color: var(--tb-finance-positive, #2e7d32); background: color-mix(in srgb, var(--tb-finance-positive, #2e7d32) 16%, transparent); }
    .sms .from { grid-row: 2; font-size: 0.72rem; font-weight: 600; color: var(--tb-text-primary, #333); }
    .sms .ts { grid-column: 2; grid-row: 2; font-size: 0.68rem; color: var(--tb-text-muted, #999); white-space: nowrap; }
    .sms .code { grid-column: 3; grid-row: 2 / 4; align-self: center; font-size: 1.2rem; font-weight: 700; letter-spacing: 0.08em; font-variant-numeric: tabular-nums; color: var(--tb-text-primary, #333); }
    .sms .operation { grid-column: 1 / 3; grid-row: 3; font-size: 0.72rem; font-weight: 600; color: var(--tb-text-primary, #333); }
    .sms .note { grid-column: 1 / 3; grid-row: 4; font-size: 0.72rem; color: var(--tb-text-muted, #777); }
    .sms--approve { border-color: color-mix(in srgb, var(--tb-finance-positive, #2e7d32) 45%, var(--tb-border-line, #ccc)); }
    .sms--consumed { opacity: 0.55; cursor: default; }
    .sms:disabled { cursor: default; }
    .sms .consumed-badge { grid-column: 3; grid-row: 1; justify-self: end; font-size: 0.62rem; font-weight: 700; text-transform: uppercase; color: var(--tb-text-muted, #777); }

    /* -- HTTP / event logs ------------------------------------------------------- */
    .log h2 { flex: 0 0 auto; }
    .log-body { flex: 1; min-height: 0; overflow: auto; padding-right: 0.1rem; }
    .log .row { display: flex; gap: 0.4rem; font-size: 0.72rem; padding: 0.2rem 0; border-bottom: 1px solid var(--tb-border-line, #eee); font-family: ui-monospace, monospace; }
    .log .row.bad { color: var(--tb-finance-negative, #c00); }
    .log .m { font-weight: 700; min-width: 38px; }
    .log .u { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .log .ty { font-weight: 700; min-width: 90px; }
    .log .su { flex: 1; }
  `,
})
export class DebugShellComponent implements OnInit, DebugDrawersController {
  readonly personas = inject(PersonaService);
  readonly phone = inject(MockPhoneService);
  readonly httpLog = inject(HttpLogService);
  readonly bankFeed = inject(BankFeedService);
  /** Test-account roster + live session counts (notes/debug-wrapper-v2.md). Read-only. */
  readonly testAccounts = inject(TestAccountsService);
  /** The test account whose info card is open, or null. */
  readonly accountInfoFor = signal<string | null>(null);
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly paymentDrafts = inject(PaymentDraftService);
  private loginCopy: Subscription | null = null;
  private readonly routeUrl = toSignal((this.router.events ?? of()).pipe(filter(e => e instanceof NavigationEnd), map(e => (e as NavigationEnd).urlAfterRedirects)), { initialValue: isOpenRoute(window.location.pathname) ? window.location.pathname + window.location.search : (this.router.url ?? '/') });
  readonly onboardingRoute = computed(() => isOpenRoute(this.routeUrl()));
  private readonly demoPersonaSwitcher = inject(DemoPersonaSwitchService);
  private readonly stepUpCeremony = inject(StepUpCeremonyService);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly demoBootstrapApplies =
    typeof window !== 'undefined' && personaSwitchAppliesToHost(window.location.hostname);
  private bankFeedPending = false;

  readonly active = this.personas.active;
  readonly phase = signal<Phase>('login');

  /** Usernames recently signed in on this debug host (localStorage; see recent-accounts.ts). */
  readonly recentUsernames = signal<string[]>(readRecentAccounts());

  /**
   * The picker's groups: recently used first, then customers, then bank staff. The audience comes
   * from the live test-account roster, else the catalog audience /bff/debug/users carries, else a
   * specific role. With none of those the account goes under "Other accounts", never staff: a
   * late or failed roster must not file a customer as bank staff.
   */
  readonly pickerGroups = computed(() => {
    const audiences = new Map<string, PickerAudience>();
    const notReady = new Set<string>();
    for (const account of this.testAccounts.roster()) {
      if (account.audience === 'customer' || account.audience === 'staff') audiences.set(account.username, account.audience);
      if (!account.signInAvailable) notReady.add(account.username);
    }
    const current = this.personas.loadState() === 'ok' && this.testAccounts.loadState() === 'ok';
    return groupPickerAccounts(current ? this.personas.personas().filter((p) => !notReady.has(p.username)) : [], this.recentUsernames(),
      (p) => audiences.get(p.username) ?? audienceOfPersona(p));
  });
  readonly curatorLine = curatorLine;
  accountMetadata(username: string) { return this.testAccounts.roster().find(row => row.username === username); }

  /** Every row in screen order: the arrow-key order of the account radio group. */
  readonly pickerOrder = computed(() => {
    const g = this.pickerGroups();
    return [...g.recent, ...g.customers, ...g.staff, ...g.other];
  });

  /** The one row in the tab order (roving tabindex): the selected account, else the first. */
  readonly accountTabStop = computed<string | null>(() => {
    const order = this.pickerOrder();
    const selected = this.active()?.id;
    return selected && order.some((p) => p.id === selected) ? selected : order[0]?.id ?? null;
  });
  readonly isSelectedAccount = (persona: Persona) => persona.username === this.active()?.username;

  /** True while the account has live sessions: only then does its row show the kind lines. */
  isLive(username: string): boolean {
    const activity = this.testAccounts.activityByUsername().get(username);
    return !!activity && activity.status === 'ok' && activity.total > 0;
  }

  readonly accountLabel = splitAccountLabel;
  readonly loggingIn = signal(false);
  readonly loginError = signal<string | null>(null);
  readonly logoutError = signal(false);

  /**
   * What the ONE session probe this load issues has told us so far.
   *
   * 'checking' until the probe answers, then 'live' (a real BFF cookie session exists for this
   * browser) or 'none'. This is the signal the left panel keys its whole face off: a live session
   * gets the signed-in face, never the sign-in picker.
   */
  readonly sessionState = signal<'checking' | 'live' | 'none'>('checking');
  /** True while POST /logout is in flight from this panel's Sign out / Switch user. */
  readonly endingSession = signal(false);
  /** The ACTUAL IdP username the probe reported, before roster-label enrichment. */
  private readonly probedUsername = signal<string | null>(null);
  /** The demo persona label the probe reported (demo hosts only; null elsewhere). */
  private readonly probedPersonaLabel = signal<string | null>(null);
  /** The tester asked for the picker back ("Switch user") -- shown before POST /logout answers. */
  private readonly switchRequested = signal(false);
  // Server-confirmed only. The picker selection is a request, never identity evidence.
  readonly actualUsername = computed<string | null>(() => this.probedUsername()?.trim() || null);

  /**
   * The ACTUAL IdP user's display label, from whichever source knows it.
   *
   * After an interactive pick it is the roster entry the tester clicked. On a COLD load that
   * resumed a pre-existing session there was never a click, so it comes from the probe's
   * `realUsername`, enriched to the roster label when the roster (which loads in parallel) has a
   * matching entry -- computed, not snapshotted, so a late roster arrival still upgrades "alice"
   * to "Alice - ACME Inc. (maker)".
   */
  readonly actualUserLabel = computed<string | null>(() => {
    const username = this.actualUsername();
    if (!username) return null;
    const lower = username.toLowerCase();
    const match = this.personas.personas().find(
      (p) => p.username?.toLowerCase() === lower || p.id?.toLowerCase() === lower,
    );
    return match?.label ?? username;
  });

  /**
   * The IN-PLATFORM persona currently being acted as, or null when this host has no persona layer
   * (dev/sim) or none is bound yet. Prefers the nested portal's own live switcher state (so the
   * label follows a mid-session "Switch Demo User" hop) and falls back to what the load-time probe
   * reported. Read defensively: several existing unit fixtures stub DemoPersonaSwitchService down
   * to `{ bootstrapReady }` only.
   */
  readonly demoPersonaLabel = computed<string | null>(() => {
    const read = (this.demoPersonaSwitcher as unknown as { activePersona?: unknown }).activePersona;
    const live = typeof read === 'function'
      ? (read as () => { label?: string | null } | null)()?.label?.trim() || null
      : null;
    return live ?? this.probedPersonaLabel();
  });

  /**
   * Render the signed-in face instead of the sign-in picker.
   *
   * Requires a NAMEABLE actual user: the townsim iframe path (enterPortalSilent) mounts the portal
   * without ever identifying an actual user, and a face that says "Signed in as actual user "
   * with nothing after it is worse than the picker. `switchRequested` lets the tester pull the
   * picker back without waiting for POST /logout to answer.
   */
  readonly signedIn = computed<boolean>(() =>
    !this.switchRequested()
    && this.actualUserLabel() !== null
    && (this.sessionState() === 'live' || this.phase() === 'portal'),
  );
  readonly targetOptions = buildTargetOptions(isDemoDebugHost());
  readonly simulatorTarget = SIMULATOR_TARGET;
  readonly target = signal<DebugSurfaceTarget>(initialTarget());

  /** Truthful label for whichever target id is currently selected (mirrors targetOptions'
   * label, which already swaps with buildTargetOptions' inline/handoff id swap per host). */
  private readonly targetLabel = computed<string>(() => {
    const t = this.target();
    if (t === this.simulatorTarget.id) return this.simulatorTarget.label;
    return this.targetOptions.find((o) => o.id === t)?.label ?? this.targetOptions[0].label;
  });

  /** Config-driven presentation (title/subtitle/icon/accent/wordmark) for the currently-selected
   * target; drives the CENTER panel's shared <tb-login-screen>. */
  readonly presentation = computed<LoginPresentation>(
    () => this.presentationFor(this.targetLabel()),
  );

  /** Sign-in page notice: the flag message, else the env advisory (debug-login-notice.ts). */
  private readonly loginNotices = inject(LoginNoticeService);
  private readonly drawerStacked = drawerStackedSignal();
  readonly loginNotice = computed(() => composeDebugLoginNotice(this.loginNotices.notice(), this.loginNotices.debugWrapper(), this.drawerStacked()));

  /** Same lookup, exposed for the LEFT panel's picker-button icons (keyed by label). */
  presentationFor(label: string): LoginPresentation {
    return LOGIN_PRESENTATION[label] ?? DEFAULT_LOGIN_PRESENTATION;
  }

  /**
   * Screen-reader label for one mock-phone message. Sighted users get the kind badge + operation
   * line + note text visually; this collapses the same facts (kind, operation, note, and whether
   * it has already been tapped) into one sentence so the distinction between an approve-only push
   * and a typed code survives without vision — the exact ambiguity 2026-09-20 xt evidence found
   * ("it was unclear which one the 6-digit field expected") when two concurrent messages looked
   * interchangeable.
   */
  phoneMessageAriaLabel(m: PhoneMessage): string {
    const action = m.kind === 'APPROVE' ? 'Tap to approve' : `Code ${m.code}`;
    const parts = [action, m.operation, m.note].filter((p): p is string => !!p);
    return m.consumed ? `${parts.join(' — ')} — already used` : parts.join(' — ');
  }

  readonly drawerProgress = signal(1);
  readonly drawersOpen = computed(() => this.drawerProgress() === 1);
  readonly drawersHidden = computed(() => this.onboardingRoute() || (this.phase() === 'portal' && this.drawerProgress() < 1));

  /**
   * True while an operation-bound step-up ceremony is on screen inside the portal.
   *
   * Drives `.shell--ceremony`, which is the ONLY condition under which the left drawer out-ranks
   * the portal's modal tier — see the `.shell--ceremony` rule in this component's styles for why
   * that stacking exemption is what makes the mock phone tappable at all.
   */
  readonly ceremonyOpen = computed(
    () => this.phase() === 'portal' && this.stepUpCeremony.pendingCeremony() !== null,
  );
  private restoreDrawersAfterCeremony: boolean | null = null;

  /** {@link DebugDrawersController.toggle} — the top-bar `manage_accounts` icon's click handler. */
  toggle(): void {
    this.restoreDrawersAfterCeremony = null;
    this.setDrawersOpen(!this.drawersOpen());
  }

  closeDrawers(): void {
    if (this.phase() !== 'portal') return;
    this.restoreDrawersAfterCeremony = null;
    this.setDrawersOpen(false);
  }

  focusDrawerHandle(event: Event): void {
    if (this.onboardingRoute() || this.phase() !== 'portal') return;
    event.preventDefault();
    this.restoreDrawersAfterCeremony = null;
    this.setDrawersOpen(true);
    this.element.nativeElement.querySelector<HTMLButtonElement>('.drawer-handle')?.focus();
  }

  private setDrawersOpen(open: boolean): void {
    if (!open && this.element.nativeElement.querySelector('aside:focus-within')) {
      this.element.nativeElement.querySelector<HTMLButtonElement>('.drawer-handle')?.focus();
    }
    this.drawerProgress.set(open ? 1 : 0);
  }

  readonly loginForm = new FormGroup({
    username: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  /** The shared login screen's injected submit strategy for this surface: POST /bff/debug/login
   * (or create a cross-origin handoff) for the selected seeded actual user. */
  readonly loginSubmitStrategy: LoginSubmitStrategy = { submit: () => this.submitLogin() };

  constructor() {
    effect(() => {
      this.testAccounts.roster();
      if (this.testAccounts.loadState() === 'ok') untracked(() => this.personas.loadPersonas());
    });
    // The wrapper is the portal's parent, so its activity feed is not covered by AppComponent's
    // root conditional. On debug-demo, defer that actual-user-scoped notification read until the
    // nested portal has verified its demo persona. Other debug surfaces retain immediate startup.
    effect(() => {
      if (!this.demoPersonaSwitcher.bootstrapReady() || !this.bankFeedPending) return;
      this.bankFeedPending = false;
      this.bankFeed.start();
    });
    // Reveal both drawers for a new operation-bound challenge, including in TownSim.
    // Keeping the portal mounted preserves the operation and its form while the phone is used.
    effect(() => {
      if (this.phase() !== 'portal') return;
      if (this.stepUpCeremony.pendingCeremony() !== null) {
        this.restoreDrawersAfterCeremony = untracked(() => this.drawersOpen());
        this.setDrawersOpen(true);
      } else if (this.restoreDrawersAfterCeremony !== null) {
        this.setDrawersOpen(this.restoreDrawersAfterCeremony);
        this.restoreDrawersAfterCeremony = null;
      }
    });
  }

  ngOnInit(): void {
    // Owner UX request: the dynamic user-picker roster fetch is the FIRST thing the page kicks
    // off on load -- ahead of even the sim-root redirect check below, so it's already in flight
    // regardless of which branch this load takes.
    this.personas.loadPersonas();
    this.loginNotices.ensureLoaded();
    // Live session indicator: refreshes on a timer for as long as the wrapper is open. Awareness
    // only -- it never blocks a sign-in.
    this.testAccounts.start();
    this.destroyRef.onDestroy(() => this.testAccounts.stop());
    this.destroyRef.onDestroy(() => this.loginCopy?.unsubscribe());
    // Public onboarding owns authentication; never restore a debug session over its URL.
    if (this.onboardingRoute()) { this.sessionState.set('none'); return; }

    // Sim-host root -> townsim. A fresh top-level visit to a sim host (e.g. http://debug.sim.localhost/)
    // is the townsim entry, not the bank persona-picker. Redirect to /town/ (a full-page navigation --
    // /town/ is a separate nginx-served static app, NOT an Angular route) before booting the rest of the
    // shell so we don't flash the picker or fetch session data we're about to leave. The guard excludes
    // the portal iframe (?townsim_portal=1), an already-active portal tab, and /town/ itself.
    if (shouldRedirectSimRootToTown()) {
      this.navigateToTown('/town/');
      return;
    }
    // D3 fix: if the ?townsim_portal=1 signal is present (set by world-app.mjs when loading us as
    // the portal iframe after a townsim-org swap), validate the BFF swap state and jump straight to
    // the customer shell. This path is EXCLUSIVE of resumePortalFromSession (both must not run, since
    // that path checks sessionStorage which won't be set in a fresh cross-origin iframe context).
    if (hasTownsimPortalParam()) {
      // The iframe path identifies itself by URL param, not by a cookie-session probe, and owns
      // its own fall-through to the picker. Nothing here is "checking".
      this.sessionState.set('none');
      this.resumePortalFromTownsimSwap();
    } else {
      this.resumePortalFromSession();
    }
  }

  /** A cross-document handoff can be restored from BFCache with its pending flag still set. */
  @HostListener('window:pageshow', ['$event'])
  onPageShow(event: PageTransitionEvent): void {
    if (event.persisted) this.loggingIn.set(false);
  }

  /** Open the read-only info card for a test account. Never signs anyone in. */
  openAccountInfo(username: string): void {
    this.accountInfoFor.set(username);
  }

  selectPersona(p: Persona): void {
    if (this.phase() !== 'login' || this.sessionState() === 'checking' || this.endingSession() || this.loggingIn()
        || !this.pickerOrder().some(row => row.id === p.id && row.username === p.username)) {
      return; // locked once login is submitted -- for the rest of the session
    }
    this.personas.select(p);
    this.loginForm.controls.username.setValue(p.username);
    this.revealAccountRow(p.username);
    this.revealContinueOnHandset();
  }

  /**
   * Arrow keys move through the account radio group and select (radio semantics); Home and End
   * jump to the ends. Focus follows without scrolling the drawer; the list scrolls itself.
   */
  onAccountListKeydown(event: KeyboardEvent): void {
    const from = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-account-radio]');
    if (!from) return;
    const order = this.pickerOrder();
    const at = order.findIndex((p) => p.id === from.dataset['accountRadio']);
    if (at < 0) return;
    let next: number;
    switch (event.key) {
      case 'ArrowDown': case 'ArrowRight': next = (at + 1) % order.length; break;
      case 'ArrowUp': case 'ArrowLeft': next = (at - 1 + order.length) % order.length; break;
      case 'Home': next = 0; break;
      case 'End': next = order.length - 1; break;
      default: return;
    }
    event.preventDefault();
    const target = order[next];
    this.selectPersona(target);
    const button = this.element.nativeElement.querySelector<HTMLElement>(
      `[data-account-radio="${CSS.escape(target.id)}"]`);
    button?.focus({ preventScroll: true });
  }

  /** Enter on the already-selected row signs in; on any other row it only selects. */
  onAccountEnter(event: Event, p: Persona): void {
    if (this.active()?.id !== p.id) return; // the button's own click selects it
    event.preventDefault();
    this.submitLogin();
  }

  /**
   * Keep the selected row in view inside the scrolling account list. Scrolls the list only (not
   * scrollIntoView, which would also scroll the drawer and the page around it). The sticky group
   * header covers the list's top, so "in view" means below it; a row taller than the room under
   * the header is aligned by its top, so its name and pill stay visible.
   */
  private revealAccountRow(username: string): void {
    if (typeof requestAnimationFrame === 'undefined') return;
    requestAnimationFrame(() => {
      const list = this.element.nativeElement.querySelector<HTMLElement>('.account-scroll');
      const row = list?.querySelector<HTMLElement>(`[data-account-row="${CSS.escape(username)}"]`);
      if (!list || !row) return;
      const listBox = list.getBoundingClientRect();
      const rowBox = row.getBoundingClientRect();
      const header = list.querySelector<HTMLElement>('.account-group')?.offsetHeight ?? 0;
      const top = listBox.top + list.clientTop + header;
      const bottom = listBox.top + list.clientTop + list.clientHeight;
      if (rowBox.top < top || rowBox.height > bottom - top) list.scrollTop -= top - rowBox.top;
      else if (rowBox.bottom > bottom) list.scrollTop += rowBox.bottom - bottom;
    });
  }

  /**
   * On a handset the login card sits below the drawer; after a selection, bring its Continue
   * button into view by scrolling the centre column only.
   */
  private revealContinueOnHandset(): void {
    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') return;
    if (!window.matchMedia?.('(max-width: 640px)').matches) return;
    requestAnimationFrame(() => {
      const center = this.element.nativeElement.querySelector<HTMLElement>('.center');
      const action = center?.querySelector<HTMLElement>('tb-login-screen tb-button');
      if (!center || !action) return;
      const centerBox = center.getBoundingClientRect();
      const actionBox = action.getBoundingClientRect();
      const viewportBottom = Math.min(centerBox.bottom, window.innerHeight);
      if (actionBox.bottom > viewportBottom) center.scrollTop += actionBox.bottom - viewportBottom + 8;
    });
  }

  /** Remember a server-confirmed debug sign-in on this host (usernames only). */
  private rememberSignIn(username: string): void {
    this.recentUsernames.set(recordRecentAccount(username, this.personas.personas().map((p) => p.username)));
  }

  selectTarget(target: DebugSurfaceTarget): void {
    if (this.phase() !== 'login' || this.sessionState() === 'checking' || this.endingSession() || this.loggingIn()) {
      return;
    }
    this.target.set(target);
  }

  /**
   * Pull the sign-in picker back to try a DIFFERENT actual user.
   *
   * Reveals the picker immediately (optimistically), then ends the current BFF session underneath
   * it: `/bff/debug/login` mints a session for the user you pick next, and leaving the previous
   * one live while the portal is torn down would leave the two identity layers disagreeing. The
   * optimistic reveal is the only difference from {@link signOut} -- the tester can read the
   * roster while the logout round-trip completes rather than watching a spinner.
   */
  switchUser(): void {
    this.switchRequested.set(true);
    this.endSessionThenShowPicker();
  }

  /** End the BFF session and return to the sign-in picker (the escape hatch that makes the
   * always-resume behaviour safe -- a stale or broken session can never trap the visitor). */
  signOut(): void {
    this.endSessionThenShowPicker();
  }

  private endSessionThenShowPicker(): void {
    if (this.endingSession()) return;
    this.logoutError.set(false);
    this.endingSession.set(true);
    this.api.logout('user').subscribe({
      next: (result) => {
        this.paymentDrafts.clearOnLogout();
        this.resetToPicker();
        // Match the working header: only the acknowledged BFF response ends this session,
        // and its destination completes any required IdP logout before another sign-in.
        if (result?.logoutUrl) window.location.href = result.logoutUrl;
      },
      error: () => {
        this.endingSession.set(false);
        this.switchRequested.set(false);
        this.logoutError.set(true);
      },
    });
  }

  private resetToPicker(errorMessage: string | null = null): void {
    this.loginCopy?.unsubscribe();
    this.loginCopy = null;
    this.bankFeed.stop();
    // The wrapper and portal remount, but both the demo switcher and AppStore are root singletons.
    // Clear the old actual user's prepared session only after the logout round-trip has settled.
    this.demoPersonaSwitcher.resetForActualUserChange();
    this.bankFeedPending = false;
    this.endingSession.set(false);
    this.switchRequested.set(false);
    this.probedUsername.set(null);
    this.probedPersonaLabel.set(null);
    this.sessionState.set('none');
    clearDebugPortalActiveFlag();
    this.personas.reset();
    this.loginForm.reset();
    this.loginError.set(errorMessage);
    this.loggingIn.set(false);
    // Unmounts <tb-root>; the picker is a login-phase surface.
    this.phase.set('login');
    this.setDrawersOpen(true);
  }

  submitLogin(): void {
    if (this.sessionState() === 'checking' || this.endingSession() || this.loggingIn()) return;
    if (!this.pickerOrder().some(this.isSelectedAccount)) { this.loginError.set('Refresh current test accounts before signing in.'); return; }
    if (!this.active() || this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    this.loginAs(this.active()!);
  }

  /**
   * Establish the selected real user's BFF session and render the portal directly.
   */
  private navigateToTown(baseUrl: string): void {
    this.api.townsimEntryState().pipe(timeout(2500)).subscribe({
      next: state => window.location.assign(debugTownsimEntryUrl(baseUrl, state.authority, state.health)),
      error: () => window.location.assign(baseUrl),
    });
  }

  private loginAs(p: Persona): void {
    this.loginCopy?.unsubscribe();
    this.loginCopy = null;
    this.loggingIn.set(true);
    this.loginError.set(null);
    const target = this.target();
    if (target === 'simulator') {
      // No backend handoff-ticket target exists yet for the simulator lane (see
      // debugSimHostUrl's docstring -- backend follow-up). Hand off with a plain top-level
      // navigation to the TownSim static app on the sibling sim host.
      this.navigateToTown(debugSimHostUrl());
      return;
    }
    if (target !== 'customer') {
      this.personas.createHandoff(p, target).subscribe({
        next: (handoff) => {
          // The server accepted this user for the handoff; remember it before leaving the page.
          this.rememberSignIn(p.username);
          window.location.assign(handoff.redirectUrl);
        },
        error: () => {
          this.loggingIn.set(false);
          clearDebugPortalActiveFlag();
          this.loginError.set('Debug handoff is unavailable for this host or user.');
        },
      });
      return;
    }
    this.personas.loginVerified(p).subscribe({
      next: (identity) => {
        const username = typeof identity?.username === 'string' ? identity.username.trim() : '';
        const subject = typeof identity?.subject === 'string' ? identity.subject.trim() : '';
        if (!username || !subject || username.toLowerCase() !== p.username.trim().toLowerCase()) {
          this.loggingIn.set(false);
          clearDebugPortalActiveFlag();
          const message = 'Debug login returned an unexpected actual user. Please try again.';
          // The response means a cookie session may already have been installed. Remove it before
          // allowing another selection; a rejected identity must not remain live behind the picker.
          this.endingSession.set(true);
          this.api.logout('forced').subscribe({
            next: () => this.resetToPicker(message),
            error: () => this.resetToPicker(message),
          });
          return;
        }
        this.probedUsername.set(username);
        this.rememberSignIn(username);
        this.enterPortal();
      },
      error: (error: HttpErrorResponse) => {
        this.loggingIn.set(false);
        clearDebugPortalActiveFlag();
        const problem = parseProblemDetails(error);
        if (problem.code === 'TB-AUTH-0006') {
          const key = messageKeyBaseFor(problem);
          // This lazy scope may not have loaded on the debug login surface. Keep the subscription
          // for language changes; a new attempt, reset or destruction cancels this old error copy.
          this.loginCopy = this.transloco.selectTranslate<string[]>([`${key}.title`, `${key}.detail`], {}, 'ui')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(copy => this.loginError.set(copy.join(' ')));
        } else {
          this.loginError.set('Debug login is unavailable for this host or user.');
        }
      },
    });
  }

  /**
   * Read the LIVE session first, on every load, before deciding what the left panel shows.
   *
   * BUG (owner-reported 2026-09-12, reproduced in 18 of 18 exploratory runs): this used to bail
   * out unless `sessionStorage['tb-debug-portal-active']` was set -- a per-TAB flag written only
   * by {@link enterPortal}. Cookies (SESSION + XSRF) are per-ORIGIN and long-lived; sessionStorage
   * is per-tab and dies with it. So every browser that held a perfectly live BFF session but no
   * flag -- a new tab, a restored profile, a fresh automation context reusing a cookie jar, any
   * tab that never itself performed the sign-in -- got no probe at all and was shown "Pick a
   * target portal and a user to sign in" while `/home` would have rendered real data. Observed
   * consequences: testers "signed in again", picked "Main Portal", and were handed off to
   * toybank.localhost (a DIFFERENT origin), abandoning the debug demo session entirely.
   *
   * The flag's original purpose was "never auto-jump into a broken portal and trap the visitor on
   * the login picker". That guard is now carried by the signed-in face itself, which always offers
   * Sign out / Switch user (see {@link signOut} / {@link switchUser}): the visitor can always get
   * back to the picker, so gating the probe on the flag buys nothing and costs the bug above.
   *
   * SCOPE -- demo hosts only, deliberately. A demo host has a purpose-built, UNFENCED BFF view
   * whose entire job is "does a live root session exist" (`GET /bff/demo/session`), and the
   * wrapper is already required to use it there because DemoPersonaModeBoundaryFilter fences
   * `/api/me`. Dev/sim debug hosts have no such client-wired view: their only read is the BANKING
   * `/api/me`, and an authorization-shaped answer from a domain API is a weaker oracle for "is
   * this browser signed in" than a session view is -- the same distinction
   * resumePortalFromDemoSession's own doc comment turns on. They therefore keep the original
   * per-tab gate until a BFF-level liveness read is wired for them too (`GET /bff/session/swap`
   * already answers 401/`realPrincipal` on every host and is the obvious candidate). Recorded as
   * an open thread rather than guessed at here.
   *
   * The flag is still WRITTEN by enterPortal and CLEARED on a dead session, because
   * {@link shouldRedirectSimRootToTown} also reads it to keep a deliberate sim-portal tab out of
   * the /town/ redirect.
   */
  private resumePortalFromSession(): void {
    // Demo-mode hosts (debug.demo.localhost) must not probe raw /api/me here -- see
    // resumePortalFromDemoSession for why. They are ALSO the only hosts that probe
    // unconditionally: see the scoping note in this method's doc comment.
    if (this.demoBootstrapApplies) {
      this.resumePortalFromDemoSession();
      return;
    }
    // Dev/sim debug hosts keep the original per-tab gate (see the doc comment above for why this
    // is scoped rather than global).
    if (!hasDebugPortalActiveFlag()) {
      this.sessionState.set('none');
      return;
    }
    this.api.me().pipe(timeout(SESSION_PROBE_TIMEOUT_MS)).subscribe({
      next: (me) => {
        if (hasAuthenticatedSubject(me)) {
          // Dev/sim debug hosts have no persona layer (personaSwitchAppliesToHost is false), so
          // there is exactly one identity to name here.
          this.adoptLiveSession(this.actualUsernameOf(me), null);
          return;
        }
        this.markNoSession();
      },
      error: () => {
        this.markNoSession();
      },
    });
  }

  /** Best-effort ACTUAL-user name from `/api/me` for the non-demo debug hosts' signed-in face. */
  private actualUsernameOf(me: Me): string | null {
    const identity = me as Me & { preferredUsername?: string | null; username?: string | null };
    for (const candidate of [identity.preferredUsername, identity.username, identity.name]) {
      if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
    }
    return null;
  }

  /** A live session was proven: name both identity layers and mount the portal. */
  private adoptLiveSession(username: string | null, personaLabel: string | null): void {
    this.probedUsername.set(username?.trim() || null);
    this.probedPersonaLabel.set(personaLabel?.trim() || null);
    this.sessionState.set('live');
    this.enterPortal();
  }

  /** No live session (or a definitively dead one): fall back to the sign-in picker. */
  private markNoSession(): void {
    this.probedUsername.set(null);
    this.probedPersonaLabel.set(null);
    this.sessionState.set('none');
    clearDebugPortalActiveFlag();
  }

  /**
   * Demo-mode resume (debug.demo.localhost, ADR-0106).
   *
   * BUG (owner-reported 2026-08-17): a demo/debug-demo host is persona-only --
   * DemoPersonaModeBoundaryFilter fences every /api/** request (including /api/me) until a
   * dedicated demo persona is bound for THIS page load ("there is deliberately no root /api/me
   * exception" -- the SPA is supposed to defer that probe until after the persona auto-swap/
   * re-entry completes). resumePortalFromSession's plain /api/me call ignored that boundary: on a
   * hard reload the just-reconstructed DebugShellComponent has not (yet) re-entered any persona
   * for this load -- even though the SERVER still durably holds the session's persona -- so the
   * raw /api/me fired as root and 403'd. The error handler then cleared the resume flag and
   * bounced the user to the login picker: an unwanted forced logout on every refresh with a demo
   * persona selected (matches the customer-portal /home hard-reload 403 the surface-matrix e2e
   * fix (option B) worked around by avoiding the reload entirely -- this is the underlying
   * product fix that fix left as a follow-up).
   *
   * FIX: probe the UNFENCED GET /bff/demo/session view instead (ApiService.demoSessionLiveness).
   * It reports whether a live root (actual-user) session exists WITHOUT requiring an
   * already-bound persona. When one exists, resume the portal and let the nested <tb-root>
   * (AppComponent.ngOnInit -> DemoPersonaSwitchService.ensureLoaded) perform the real,
   * server-verified persona re-entry against the durable session state -- the exact path a fresh
   * visit already uses successfully, restoring the ALREADY-SELECTED persona (not just the
   * default) because ensureLoaded/acceptPreparedSession treat a previously-switched persona and
   * an auto-entered default persona identically. Only a genuinely dead/absent root session clears
   * the flag and falls back to the picker, preserving the original "never trap in a broken
   * portal" intent.
   *
   * REGRESSION (observed live, ADR-0133 N0 tour): the stated intent above was violated by the
   * implementation. The `error` callback fired — and cleared the flag, bouncing to the picker —
   * on ANY HTTP failure from this probe, not only a genuinely dead session: a transient 5xx or
   * network hiccup reading `/bff/demo/session` (this box runs 45+ services plus concurrent
   * Playwright/agent load; edge-bff logs show the SAME class of transient "acting context
   * changed"/late-fence denial racing sibling `/home` requests elsewhere) reads identically to a
   * genuinely absent session, even though the server-side persona/root session was confirmed
   * fine seconds later. A definitive "not logged in" answer from THIS endpoint is a 401
   * (unauthenticated at the BFF cookie layer); anything else (5xx, network error, an unexpected
   * status) is not proof of a dead session and deserves one retry before trapping the visitor in
   * the picker over a blip that a moment later resolves cleanly — the same principle
   * GuidedOnboardingChecklistComponent.load() now applies to its own `/api/quest/me` read.
   */
  private resumePortalFromDemoSession(): void {
    this.api.demoSessionLiveness().pipe(
      timeout(SESSION_PROBE_TIMEOUT_MS),
      retry({
        count: 1,
        delay: (error: HttpErrorResponse) => shouldRetryDemoSessionProbe(error?.status)
            ? timer(600)
            : throwError(() => error),
      }),
    ).subscribe({
      next: (session) => {
        // The anonymous answer ({authenticated:false}) is the definitive "no live root session"
        // on a demo host now that the probe no longer 401s for an anonymous visitor.
        if (session.authenticated === false) {
          this.markNoSession();
          return;
        }
        if (session.enabled && !!session.realSubject?.trim()) {
          // The SAME unfenced view already carries both identity layers (DemoSessionView:
          // realUsername + activePersona), so naming them needs no extra call and no new endpoint.
          this.adoptLiveSession(session.realUsername ?? null, session.activePersona?.label ?? null);
          return;
        }
        this.markNoSession();
      },
      error: () => {
        this.markNoSession();
      },
    });
  }

  /**
   * D3 fix: townsim portal iframe bootstrap.
   *
   * Called ONLY when ?townsim_portal=1 is present in the URL (set by world-app.mjs after a
   * successful TownSim session activation). Validates the browser-safe live BFF state via
   * GET /bff/townsim/session:
   * - active + TOWNSIM_ORG + PERSONA -> jump directly to the customer shell (no picker).
   * - Anything else (NONE, 401, network error) -> fall through to the normal login picker so the
   *   user is never silently trapped without a way to sign in.
   *
   * This two-layer check (explicit URL signal + BFF swap state) prevents the r22 regression:
   * a stale BFF cookie with swapKind NONE (no active townsim swap) falls through to the picker.
   * A fresh top-level debug.sim.localhost visit never carries ?townsim_portal=1 (no bypass).
   */
  private resumePortalFromTownsimSwap(): void {
    this.api.currentTownsimSession().subscribe({
      next: (session) => {
        if (isActiveTownsimSession(session)) {
          // Active townsim-org swap confirmed by the BFF — skip the picker, load the customer shell.
          // Do NOT set tb-debug-portal-active (that flag governs the human-initiated top-level portal
          // flow; this is the iframe path and does not need to survive a reload in that sense).
          this.enterPortalSilent();
        }
        // Otherwise fall through: swapKind NONE or not authenticated → stay on the login picker.
      },
      error: () => {
        // 401 / network error -> BFF swap cannot be confirmed; stay on the login picker.
      },
    });
  }

  private enterPortal(): void {
    // A human login on the sim host enters the renderer. The portal iframe is a separate
    // ?townsim_portal=1 path and must keep its Angular shell after session activation.
    if (isSimHost() && !hasTownsimPortalParam()) {
      this.loggingIn.set(false);
      window.location.assign('/town/');
      return;
    }
    this.phase.set('portal');
    // Mounting the portal IS proof of a live session, whether it came from the load-time probe or
    // from an interactive sign-in a moment ago; keep the left panel's face consistent either way.
    this.sessionState.set('live');
    this.switchRequested.set(false);
    this.loggingIn.set(false);
    this.startBankFeedWhenSafe();
    this.setDrawersOpen(false);
    setDebugPortalActiveFlag();
    // Fix: when the user re-logs in from the /signed-out page, the Angular router's current URL
    // is still /signed-out (the sign-out redirect landed here before the BFF session was cleared).
    // Mounting <tb-root> without navigating would show the SignedOutComponent again.
    // Push the router to /home so the portal opens on the Overview, not the sign-out screen.
    // Same fix applies coming FROM the unauthenticated /open KYC front-door preview
    // (openAccountPortal() below, "Back to sign-in" -> a real login): a completed sign-in from
    // there should land the picker's actual user on the ordinary portal home, not leave them
    // stuck mid-way through the public /open preview.
    if (this.router.url.startsWith('/signed-out') || this.router.url.startsWith('/open')) {
      void this.router.navigate(['/home'], { replaceUrl: true });
    }
  }

  /**
   * Owner UX request (KYC front-door overnight lane, 2026-09-23): "Open an account" is NOT a
   * login target (deliberately excluded from targetOptions/buildTargetOptions, same reasoning as
   * SIMULATOR_TARGET's docstring). It mounts the real portal (<tb-root>) with NO session so the
   * public, unauthenticated /open flow renders exactly as it would on the real customer host --
   * so, unlike enterPortal(): sessionState stays 'none' (never 'live'), the
   * tb-debug-portal-active flag is never set (that flag means "a real login happened, resume the
   * portal across reloads" -- untrue here), and route-owned onboarding presentation dismisses all debug chrome.
   */
  openAccountPortal(): void {
    if (this.phase() !== 'login' || this.endingSession() || this.loggingIn()) return;
    this.phase.set('portal');
    this.sessionState.set('none');
    // No bank feed here (unlike enterPortal()/enterPortalSilent()) -- there is no session for it
    // to read events under, and the point of this preview is the unauthenticated /open flow.
    void this.router.navigate(['/open']);
  }

  /** Leaves the unauthenticated /open preview and returns to the ordinary sign-in picker. */
  backToSignIn(): void {
    this.phase.set('login');
  }

  /**
   * D3 fix: enter the portal for the townsim iframe path.
   * Identical to enterPortal() except it does NOT set tb-debug-portal-active — that flag governs
   * the human-initiated top-level portal flow (resumePortalFromSession). The iframe is identified
   * by its ?townsim_portal=1 URL param, not sessionStorage, so the two paths stay independent.
   * Both drawers start closed, just as they do after an ordinary portal login.
   */
  private enterPortalSilent(): void {
    this.phase.set('portal');
    this.loggingIn.set(false);
    this.startBankFeedWhenSafe();
    this.setDrawersOpen(false);
  }

  private startBankFeedWhenSafe(): void {
    if (this.demoBootstrapApplies && !this.demoPersonaSwitcher.bootstrapReady()) {
      this.bankFeedPending = true;
      return;
    }
    this.bankFeed.start();
  }
}
